import { Grid } from "pepsico-ds";
import { ActivityOutcomesSummary } from "../../../../components/campaign/ActivitySetup/ActivityOutcomesSummary";
import {
  filterDisplayableActivityRules,
  isOutcomeRuleConfigured,
} from "../../../../components/campaign/campaignUtils";
import {
  isRetailerConditionRule,
  isStoreConditionRule,
} from "../../../../components/campaign/rulesAndOutcomes/outcomes/conditionTypeUtils";
import { RetailerConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/retailerOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/retailerOutcome/retailerOutcome.scss";
import { StoreConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/storeOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/storeOutcome/storeOutcome.scss";
import { SurveyConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/surveyOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/surveyOutcome/surveyOutcome.scss";
import ViewComponentsRenderer from "../../../../components/campaign/viewComponents/ViewComponentsRenderer";
import { useSweepstakeListQuery } from "../../../../hooks/useSweepstakeListQuery";
import {
  isPathInCurrentURL,
  updateSweepstakesOutcomes,
} from "../../../../utils/commons";
import { FeatureToggle, useFeatureToggle } from "../../../../utils/featureToggle";
import LeaderboardOutcomeSummary from "./LeaderboardOutcomeSummary";
import SummaryCard from "./SummaryCard";

const SURVEY_RULE_SLUG = "survey-answer-matching-rule";

const Activity = ({ data, length }) => {
  return (
    <>
      {length > 1 && (
        <h3 className="campaign-definition-name">
          {data?.activityDefinition?.name}
        </h3>
      )}
      <Grid
        item
        xs={12}
        md={6}
        xl={4}
        mt={1}
        mb={1}
        className="display-flex gap-3 two-column"
      >
        <ViewComponentsRenderer
          customComponentName={data?.activityDefinition?.slug}
          data={data?.activityData}
          inputSchema={data?.activityDefinition?.inputSchema}
          hiddenFields={["keywords"]}
        />
      </Grid>
    </>
  );
};
const Rules = ({ data }) => (
  <>
    {/* <h3 className="campaign-name">Rules</h3> */}
    {data?.map((rule) => (
      <Grid
        key={rule?.ruleDefinition?.slug}
        item
        xs={12}
        md={6}
        xl={4}
        mt={1}
        mb={1}
        className="display-flex gap-3 two-column"
      >
        <ViewComponentsRenderer
          customComponentName={rule.ruleDefinition?.slug}
          data={rule.ruleData}
          inputSchema={rule.ruleDefinition?.inputSchema}
          ruleDefinition={rule.ruleDefinition}
          hiddenFields={["keywords"]}
        />
      </Grid>
    ))}
  </>
);
const Outcomes = ({ data, listSweepstakes }) => {
  const updatedData = updateSweepstakesOutcomes(data, listSweepstakes);

  // Outcome slugs that handle their own rules rendering (via custom view components)
  const SELF_CONTAINED_OUTCOME_SLUGS = new Set([
    "award-flat-points",
    "award-purchase-based-points",
    "calculate-flat-points",
    "calculate-purchase-based-points",
    "points-based-streaks",
    "direct-redemption-rewards",
    "direct-redemption-rewards-b2b",
  ]);

  return (
    <>
      <h3 className="campaign-name">Outcomes</h3>
      {updatedData.map((outcome, outcomeIndex) => {
        const slug = outcome?.outcomeDefinition?.slug;
        const rules = outcome.outcomeRules || [];
        const surveyRule = rules.find(
          (r) => r?.ruleDefinition?.slug === SURVEY_RULE_SLUG
        );
        const retailerRule = rules.find(isRetailerConditionRule);
        const storeRule = rules.find(isStoreConditionRule);
        const shouldRenderRulesSeparately = !SELF_CONTAINED_OUTCOME_SLUGS.has(slug);

        return (
          <div key={`${slug}-${outcomeIndex}`}>
            {/* Outcome data - render with full definition for custom components */}
            {slug === "award-leaderboard-points" ? (
              <div className="additional-fields-container">
                <LeaderboardOutcomeSummary
                  outcome={outcome}
                  key={outcome?.outcomeData?.leaderboard_id}
                />
              </div>
            ) : slug &&
              outcome?.outcomeDefinition?.inputSchema &&
              outcome?.outcomeDefinition?.uiSchema ? (
              <div className="ml-3">
                <Grid
                  item
                  xs={12}
                  md={6}
                  xl={4}
                  mt={1}
                  mb={1}
                  className="display-flex gap-3 two-column"
                >
                  <ViewComponentsRenderer
                    customComponentName={slug}
                    data={outcome?.outcomeData}
                    inputSchema={outcome?.outcomeDefinition?.inputSchema}
                    uiSchema={outcome?.outcomeDefinition?.uiSchema}
                    definition={outcome}
                    hiddenFields={["keywords"]}
                  />
                </Grid>
              </div>
            ) : null}

            {/* Outcome rules - only for outcomes that don't handle rules internally */}
            {shouldRenderRulesSeparately &&
              (outcome.outcomeRules || [])
                .filter(isOutcomeRuleConfigured)
                .filter((rule) => rule?.ruleData?.target_id !== null)
                .filter((rule) => rule?.ruleDefinition?.slug !== SURVEY_RULE_SLUG)
                .filter(
                  (rule) =>
                    !isRetailerConditionRule(rule) && !isStoreConditionRule(rule)
                )
                .map((rule, ruleIndex) => (
                  <div
                    className="ml-3"
                    key={`${rule?.ruleDefinition?.slug}-${ruleIndex}`}
                  >
                    {rule?.name && (
                      <h3 className="activity-definition-name">{rule?.name}</h3>
                    )}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={4}
                      mt={1}
                      mb={1}
                      className="display-flex gap-3 two-column"
                    >
                      {!!Object.keys(rule.ruleData || {}).length && (
                        <ViewComponentsRenderer
                          customComponentName={rule.ruleDefinition?.slug}
                          data={rule.ruleData}
                          inputSchema={rule.ruleDefinition?.inputSchema}
                          uiSchema={rule.ruleDefinition?.uiSchema}
                          ruleDefinition={rule.ruleDefinition}
                          hiddenFields={["keywords"]}
                        />
                      )}
                    </Grid>
                  </div>
                ))}

            {/* Survey condition */}
            {surveyRule?.ruleData?.condition_display && (
              <SurveyConditionDisplay
                key={`survey-condition-${outcome.outcomeDefinition?.id}`}
                ruleData={surveyRule.ruleData}
                readOnly
              />
            )}
            {retailerRule && (
              <RetailerConditionDisplay ruleData={retailerRule.ruleData} readOnly />
            )}
            {storeRule && (
              <StoreConditionDisplay ruleData={storeRule.ruleData} readOnly />
            )}
          </div>
        );
      })}
    </>
  );
};

const SummaryActivitySetup = ({ data, isViewScreen, editSectionHandler }) => {
  const { sweepstakesList: list } = useSweepstakeListQuery();
  const showActivityIdAtSummaryPage = useFeatureToggle(
    FeatureToggle.showActivityIdAtSummaryPage
  );
  const isDetailedOutcomeEnabledInSummaryPage = useFeatureToggle(
    FeatureToggle.isDetailedOutcomeEnabledInSummaryPage
  );

  return (
    <SummaryCard
      title="Activity Setup"
      isViewScreen={isViewScreen}
      handleEditSection={editSectionHandler}
    >
      {data?.name && <h3 className="campaign-name">{data?.name}</h3>}
      {data.activities?.map((activity) => {
        const displayableActivityRules = filterDisplayableActivityRules(
          activity?.activityRules
        );
        const isAcitivtyEmpty =
          (activity?.activityDefinition?.rules?.length === 0 &&
            activity.activityDefinition?.outcomes?.length === 0 &&
            Object.keys(activity.activityDefinition?.uiSchema || {}).length === 0) ||
          false;
        if (isAcitivtyEmpty) {
          return (
            <h3
              key={activity?.activityDefinition?.name}
              className="campaign-definition-name"
            >
              {activity.activityDefinition.name}
            </h3>
          );
        }
        return (
          <div className="mb-20" key={activity?.activityDefinition?.name}>
            {activity?.activityDefinition?.name && (
              <h3 className="campaign-name fs-14">
                {activity?.activityDefinition?.name}
              </h3>
            )}
            <div className="ml-3">
              {showActivityIdAtSummaryPage && activity?.id && (
                <div key={activity?.id} className="additional-fields-container">
                  <div className="field-pair">
                    <span className="field-label">Activity ID:</span>
                  </div>
                  <div className="field-pair-two-column">
                    <span className="field-value">
                      {isPathInCurrentURL("/create") ? "-" : activity?.id}
                    </span>
                  </div>
                </div>
              )}
              {activity?.activityData?.label && (
                <div key={activity?.id} className="additional-fields-container">
                  <div className="field-pair">
                    <span className="field-label">Activity Label:</span>
                  </div>
                  <div className="field-pair-two-column">
                    <span className="field-value">
                      {activity?.activityData?.label}
                    </span>
                  </div>
                </div>
              )}
              <Grid container>
                {activity?.activityData &&
                  activity?.activityDefinition?.inputSchema &&
                  activity?.activityDefinition?.uiSchema && (
                    <Activity data={activity} length={data.activities?.length} />
                  )}

                {/* Render activityRules */}
                {displayableActivityRules.length > 0 && (
                  <Rules data={displayableActivityRules} />
                )}
              </Grid>
            </div>
            {/* Render activities outcomes */}
            {activity?.outcomes?.length > 0 &&
              (isDetailedOutcomeEnabledInSummaryPage ? (
                <ActivityOutcomesSummary
                  data={activity.outcomes}
                  listSweepstakes={list || []}
                />
              ) : (
                <Outcomes data={activity.outcomes} listSweepstakes={list || []} />
              ))}
          </div>
        );
      })}
    </SummaryCard>
  );
};

export default SummaryActivitySetup;
